import { FC, useCallback, useEffect } from 'react';

import { ChipChoice } from '@telialt/common-chip-choice/src';
import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { useAddLeasingPlanMutation } from '@telialt/core-eshop/src/hooks/mutations/useAddLeasingPlanMutation';
import { useDeleteLeasingPlanMutation } from '@telialt/core-eshop/src/hooks/mutations/useDeleteLeasingPlanMutation';
import { useCartQuery } from '@telialt/core-eshop/src/hooks/queries/useCartQuery';
import { ESHOP_TEST_ID } from '@telialt/core-eshop/src/test/test-ids';
import { selectIsFullPricePayment, selectPossibleLeasingPlans } from '@telialt/utils/src/eshopSelectors';

import { useLeasingRestriction } from '../useLeasingRestriction';
import { PaymentTypeSelectorWrapper } from './PredefinedLeasingSelector.styles';

export const PaymentTypeSelector: FC<{ ariaLabelledBy?: string }> = ({ ariaLabelledBy }) => {
    const { t } = useTranslations();
    const { cart } = useCartQuery();
    const { addLeasingPlan } = useAddLeasingPlanMutation();
    const restriction = useLeasingRestriction();

    const possibleLeasingPlans = selectPossibleLeasingPlans(cart);
    const firstLeasingPlanCode = possibleLeasingPlans?.[0]?.code;
    const pickInitLeasingPlan = useCallback(
        () => addLeasingPlan(firstLeasingPlanCode),
        [addLeasingPlan, firstLeasingPlanCode],
    );

    const { deleteLeasingPlan } = useDeleteLeasingPlanMutation();

    const isFullPricePayment = selectIsFullPricePayment(cart);
    const isLeasingPaymentDisabled = !!restriction;
    const isLeasingPaymentHidden = !possibleLeasingPlans?.length;

    useEffect(() => {
        if (!isFullPricePayment && isLeasingPaymentDisabled) {
            deleteLeasingPlan();
        }
    }, [deleteLeasingPlan, isFullPricePayment, isLeasingPaymentDisabled]);

    return (
        <PaymentTypeSelectorWrapper aria-labelledby={ariaLabelledBy} data-test={ESHOP_TEST_ID.PAYMENT_TYPE_SELECTOR}>
            <div>
                <ChipChoice
                    onChange={pickInitLeasingPlan}
                    checked={!isFullPricePayment}
                    disabled={isLeasingPaymentDisabled}
                    hidden={isLeasingPaymentHidden}
                    name="payment-type-selector"
                >
                    {t['eshop.product_page.leasing_selector.leasing_option']}
                </ChipChoice>
            </div>

            <div>
                <ChipChoice
                    onChange={() => deleteLeasingPlan()}
                    checked={isFullPricePayment}
                    name="payment-type-selector"
                >
                    {t['eshop.product_page.leasing_selector.full_price_option']}
                </ChipChoice>
            </div>
        </PaymentTypeSelectorWrapper>
    );
};
